import {
  accidentPaths,
  hospitalPaths,
  criticalPaths,
  dentalPaths,
  visionPaths,
  healthPaths,
} from './constants/plansStats';
import { InsurancePlan } from 'src/services/interfaces/insurance';

export const sanitize = (s?: string) =>
  (s || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

export const normalizeCategory = (cat?: string) => {
  if (!cat) return '';
  const normalized = sanitize(cat);
  if (normalized.includes('accident')) return 'accident';
  if (normalized.includes('hospital') || normalized.includes('indemnity'))
    return 'hospital';
  if (normalized.includes('critical') || normalized.includes('criticalillness'))
    return 'criticalillness';
  if (normalized.includes('majormedical')) return 'health';
  if (normalized.includes('medical')) return 'health';
  if (normalized.includes('health')) return 'health';
  if (normalized.includes('dental')) return 'dental';
  if (normalized.includes('vision')) return 'vision';
  return normalized;
};

export const getByPath = (
  obj: any,
  pathParts: Array<string | number> | undefined
) => {
  if (!obj || !pathParts || pathParts.length === 0) return undefined;
  let cur: any = obj;
  for (const part of pathParts) {
    if (cur == null) return undefined;
    cur = cur[part as any];
  }
  return cur;
};

export const formatMoneyUS = (n: number) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

export const formatValue = (label: string, val: any) => {
  if (val == null || val === '') return '-';
  if (typeof val === 'number') return formatMoneyUS(val);

  const str = String(val).trim();

  const hasPercent = str.includes('%');
  const hasDollar = str.includes('$');
  if (hasPercent && hasDollar) {
    return str;
  }

  const dollarMatch = str.match(/\$\d+(?:,\d{3})*/);
  if (dollarMatch) {
    return dollarMatch[0];
  }

  if (hasPercent) {
    return str;
  }

  if (/^\d+(\.\d+)?$/.test(str)) {
    const num = Number(str);
    if (/payout|percent/i.test(label)) return `${num}%`;
    return formatMoneyUS(num);
  }

  return str;
};

export const getPlanStats = (
  fullPlan?: InsurancePlan,
  getCriticalIllnessMaxBenefit?: () => number
): Array<{ label: string; value: string }> => {
  if (!fullPlan) return [];

  const canonical = normalizeCategory(fullPlan?.category);

  let planStats: Array<{ label: string; value: string }> = [];

  if (canonical === 'accident') {
    planStats = Object.keys(accidentPaths).map((label) => ({
      label,
      value: formatValue(label, getByPath(fullPlan, accidentPaths[label])),
    }));
  } else if (canonical === 'hospital') {
    planStats = Object.keys(hospitalPaths).map((label) => ({
      label,
      value: formatValue(label, getByPath(fullPlan, hospitalPaths[label])),
    }));
  } else if (canonical === 'criticalillness') {
    planStats = Object.keys(criticalPaths).map((label) => {
      const path = criticalPaths[label];
      return {
        label,
        value: formatValue(label, path ? getByPath(fullPlan, path) : undefined),
      };
    });
  } else if (canonical === 'dental') {
    planStats = Object.keys(dentalPaths).map((label) => ({
      label,
      value: formatValue(label, getByPath(fullPlan, dentalPaths[label])),
    }));
  } else if (canonical === 'vision') {
    planStats = Object.keys(visionPaths).map((label) => ({
      label,
      value: formatValue(label, getByPath(fullPlan, visionPaths[label])),
    }));
  } else if (canonical === 'health') {
    planStats = Object.keys(healthPaths).map((label) => {
      const path = healthPaths[label];
      if (label === 'Referrals required') return { label, value: 'No' };
      return {
        label,
        value: formatValue(label, path ? getByPath(fullPlan, path) : undefined),
      };
    });
  } else {
    planStats = [
      { label: 'Network Type', value: 'PRO' },
      { label: 'Deductible', value: '$3,500' },
      { label: 'Office visits', value: '$45' },
      { label: 'Generic Drugs', value: '$25' },
      { label: 'Referrals required', value: 'No' },
    ];
  }

  return planStats;
};
